import React, { useState, useEffect, Suspense } from "react";
import { AnimatePresence, useCycle } from "framer-motion";
import PropTypes from "prop-types";

//hooks
import useAxios from "../../hooks/useAxios";

//comps
const PageGrid = React.lazy(() => import("../PageGrid"));
const ContentGrid = React.lazy(() => import("../ContentGrid"));
const Filters = React.lazy(() => import("../Filters"));
const RidesCard = React.lazy(() => import("./RidesCard"));
const SidePanel = React.lazy(() => import("../SidePanel"));
const NoResults = React.lazy(() => import("../NoResults"));
import Loading from "../Loading";

//style rules
import { Colors } from "../../styles";

const App = ({ filtersJson, partnersJson }) => {
	const [partners] = useState(partnersJson ? JSON.parse(partnersJson) : []);
	const [filters] = useState(filtersJson ? JSON.parse(filtersJson) : []);
	const [selectedFilter, setSelectedFilter] = useState({
		slug: "all",
	});
	const [selectedRide, setSelectedRide] = useState({});
	const [open, cycleOpen] = useCycle(false, true);

	const rides = useAxios("rides-and-games");

	// useEffect responsible for displaying the sidepanel from a sharable url & initial display of rides
	useEffect(() => {
		if (rides.length > 0 && window.location.hash) {
			// we extract the artist id from the Hash
			let trimmedIncomingArtistId = window.location.hash.replace("#", "");
			// find the artist we want from the artists array using the trimmed hash aka id
			let selectedRide = rides.find(
				(ride) => ride.slug == trimmedIncomingArtistId
			);
			// we open the sidePanel
			toggleSidePanel(selectedRide);
		}
	}, [rides]);

	// handles the scroll behaviour on open and closing the sidePanel
	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "scroll";
		}
	}, [open]);

	const selectFilter = (filter) => {
		setSelectedFilter(filter);
	};

	// responsible for opening and closing the sidepanel
	// it lives in the App because it gets passed down to multiple components
	const toggleSidePanel = (ride) => {
		if (ride) {
			cycleOpen(true);
			setSelectedRide(ride);
			updateUrl(ride);
		} else {
			cycleOpen(false);
			setSelectedRide({});
			window.history.pushState({}, "", window.location.pathname);
		}
	};

	const updateUrl = (ride) => {
		let urlParams = "";
		if (ride) {
			// we use the hash param in window.location to store the ride
			urlParams = "#" + ride.slug;
		}
		window.history.pushState(null, "", urlParams);
	};

	const filterRides = () => {
		return rides.filter((ride) => ride["type-slug"] === selectedFilter.slug);
	};

	const renderRides = () => {
		if (rides.length == 0) {
			return (
				<NoResults
					color={Colors.base.white}
					text={"Stay tuned for upcoming rides"}
				/>
			);
		} else {
			if (selectedFilter.slug === "all") {
				return rides.map((ride) => {
					return (
						<RidesCard
							ride={ride}
							key={ride.id}
							toggleSidePanel={toggleSidePanel}
							isDisabled={true} // temp solution for not using sidepanels 
						/>
					);
				});
			} else {
				const filteredVendors = filterRides();
				if (filteredVendors.length) {
					return filteredVendors.map((ride) => {
						return (
							<RidesCard
								ride={ride}
								key={ride.id}
								toggleSidePanel={toggleSidePanel}
								isDisabled={true} // temp solution for not using sidepanels 
							/>
						);
					});
				} else {
					return (
						<NoResults
							color={Colors.base.white}
							text={"Stay tuned for upcoming rides"}
						/>
					);
				}
			}
		}
	};

	return (
		<section aria-label="Rides and Games Index">
			<Suspense fallback={<Loading />}>
				<PageGrid>
					<Filters
						isRides={true}
						filters={filters}
						selectedFilter={selectedFilter}
						selectFilter={selectFilter}
					/>
					<ContentGrid rowGap={"6.7rem"} padding={"12.2rem 0 12rem 0"}>
						{renderRides()}
					</ContentGrid>
				</PageGrid>

				{/* SidePanel is currently disabled for rides */}
				<AnimatePresence>
					{open && selectedRide && (
						<SidePanel
							isRide={true}
							selectedContent={selectedRide}
							toggleSidePanel={toggleSidePanel}
							partners={partners}
						/>
					)}
				</AnimatePresence>
			</Suspense>
		</section>
	);
};

export default App;

App.propTypes = {
	filtersJson: PropTypes.any,
	partnersJson: PropTypes.any,
};
